import { isValidField } from "./validation";
import { formatCompactCurrency } from "../Helper/formatCompactCurrency";

/**
 * Format currency values for display
 */
export function formatCurrency(value: any): string {
  return formatCompactCurrency(value, { fallback: "N/A" });
}

/**
 * Format percentage values for display
 */
export function formatPercentage(value: any): string {
  if (!isValidField(value)) return "N/A";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/A";

  // Handle both decimal (0-1) and percentage (0-100) formats
  const percentValue = numValue <= 1 ? numValue * 100 : numValue;
  return `${percentValue.toFixed(2)}%`;
}

/**
 * Format numeric values for display
 */
export function formatNumeric(value: any): string {
  if (!isValidField(value)) return "N/A";

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "N/A";

  return numValue.toLocaleString();
}

/**
 * Format date values for display (converts 2025-08-25 to Aug 25, 2025)
 */
export function formatDate(value: any): string {
  if (!value) return "N/A";

  try {
    let dateObj: Date;

    // Handle various date formats
    if (typeof value === "string") {
      // Handle ISO format (2025-08-25T00:00:00Z or 2025-08-25)
      if (value.includes("T") || value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateObj = new Date(value);
      }
      // Handle date ranges like "2025-08-20 to 2025-08-26"
      else if (value.includes(" to ")) {
        const [startDate] = value.split(" to ");
        dateObj = new Date(startDate.trim());
      }
      // Handle other string formats
      else {
        dateObj = new Date(value);
      }
    } else if (value instanceof Date) {
      dateObj = value;
    } else {
      return String(value);
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return String(value);
    }

    // Format as "Aug 25, 2025"
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return dateObj.toLocaleDateString("en-US", options);
  } catch (error) {
    console.error("Date formatting error:", error);
    return String(value);
  }
}

/**
 * Format date ranges for display
 */
export function formatDateRange(value: any): string {
  if (!value || typeof value !== "string") return "N/A";

  if (value.includes(" to ")) {
    const [startDate, endDate] = value.split(" to ").map(d => d.trim());
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);

    if (formattedStart !== "N/A" && formattedEnd !== "N/A") {
      return `${formattedStart} to ${formattedEnd}`;
    }
  }

  // Single date
  return formatDate(value);
}

/**
 * Get color-coded health badge styles
 */
export function getHealthBadgeColor(flag: string): string {
  switch (flag) {
    case "G":
      return "background: #dcfce7; color: #166534; border: 1px solid #bbf7d0;";
    case "Y":
      return "background: #fef3c7; color: #92400e; border: 1px solid #fde68a;";
    case "R":
      return "background: #fee2e2; color: #991b1b; border: 1px solid #fecaca;";
    default:
      return "background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db;";
  }
}

/**
 * Get color-coded relevance badge styles
 */
export function getRelevanceBadgeColor(flag: string): string {
  switch (flag) {
    case "H":
      return "background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe;";
    case "M":
      return "background: #fef3c7; color: #92400e; border: 1px solid #fde68a;";
    case "L":
      return "background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db;";
    default:
      return "background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db;";
  }
}
